import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Volume2, Layers, HelpCircle, CheckCircle2, Flame, Award, CalendarDays, BookHeart, Sparkles, Cross, ListChecks, Gamepad2 } from "lucide-react";

const ICON = "https://static.prod-images.emergentagent.com/jobs/aa10c7d5-1915-4689-8d0f-1ed7e74b35ba/images";
const BADGES = [
  { name: "First Steps", desc: "Finish your very first assignment", img: `${ICON}/9750fe9198823a9028b2c88dd45b4ffd490aa27fecfae13307f73614f7a15b56.jpeg` },
  { name: "Bookworm", desc: "Finish 5 assignments", img: `${ICON}/ca7d51607f2b98d2b12879106cd345c19769401c8537669e5fe1503093c97a4e.jpeg` },
  { name: "Scripture Scholar", desc: "Memorize your first Bible verse", img: `${ICON}/41519d61d5dae009ac772c8be51bb907fff4896a36ae4fe5081f3562ed7770f7.jpeg` },
  { name: "Faithful Reader", desc: "Do a day of your Bible reading plan", img: `${ICON}/212ab9f3a6bc0dbc4f2fa50673f40f766c2197d6c7120d7710ad7867525b7882.jpeg` },
  { name: "On Fire", desc: "Read 3 days in a row (a streak!)", img: `${ICON}/40a72be81ae18fb4d67fdf6bb3d1380d972cb88ce3099de3d7c1acdabf542d8f.jpeg` },
  { name: "Bright Star", desc: "Score 90% or higher", img: `${ICON}/52ed39ac1ba7ea48007be1d96514ae6f155ef6b5c72f38b3cda582f555ef7d5f.jpeg` },
  { name: "Scientist", desc: "Finish a Science assignment", img: `${ICON}/a2156b08839757be132390efef720fbc2b73c686b6f312071fae2fd43a0c616f.jpeg` },
  { name: "Explorer", desc: "Finish a Social Studies assignment", img: `${ICON}/42c0875c75cd095c18be15c0378abae08cc43a2c49afc550ec180aeca015ead7.jpeg` },
  { name: "Game On", desc: "Play a learning mini-game", img: `${ICON}/50b3b5c830a96f05b9f16a45e4c5dda372d2fe9afdec9ea18fc7eb0e67a621e0.jpeg` },
];

const STEPS = [
  { icon: BookOpen, color: "#81B29A", title: "1. Open a lesson", text: "On your dashboard you'll see your assignments. Tap START to open one and read the lesson." },
  { icon: Volume2, color: "#E07A5F", title: "2. Read or listen", text: "Read the summary and Key Facts. Want it read out loud? Tap \"Read lesson aloud\" — you can even pick your own narrator voice!" },
  { icon: Layers, color: "#F4A261", title: "3. Study with flashcards", text: "Tap \"Study flashcards\" to practice the vocabulary and Key Facts. Flip each card to check yourself." },
  { icon: HelpCircle, color: "#3D405B", title: "4. Stuck? Ask your tutor", text: "Tap \"I don't understand this\" for a friendly helper. It gives hints and examples — it won't just give away answers!" },
  { icon: CheckCircle2, color: "#81B29A", title: "5. Answer & submit", text: "Tap Begin Assignment, answer the questions, and hit Submit. You'll get your score right away with helpful feedback." },
];

const EXTRAS = [
  { icon: ListChecks, color: "#E07A5F", title: "Chores", text: "Finish chores your parent assigns to earn 100 points each — plus screen-time minutes you can use on phone or TV!" },
  { icon: Gamepad2, color: "#F4A261", title: "Mini-Games", text: "Play Vocabulary Match and the Timed Quiz, built from your own lessons. Play to earn the Game On badge!" },
  { icon: BookHeart, color: "#81B29A", title: "Scripture Memory", text: "Learn Bible verses (NIV), tap \"I know it!\" when you've got one, and practice with flashcards." },
  { icon: BookOpen, color: "#3D405B", title: "Bible Reading Plan", text: "Read a little each day and check it off. Read several days in a row to build a streak!" },
  { icon: CalendarDays, color: "#E07A5F", title: "School Calendar", text: "See what's coming up — your assignments show up on the days they're due." },
  { icon: Flame, color: "#F4A261", title: "Streaks", text: "Keep showing up day after day to grow your streak. Don't break the chain!" },
];

export default function StudentGuide() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";

  return (
    <div>
      <TopBar subtitle="How it works" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => navigate(isParent ? "/parent" : "/student")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Back</button>

        <div className="relative grain rounded-3xl overflow-hidden p-8 md:p-10 text-white mb-10" style={{ background: "#81B29A" }}>
          <div className="flex items-center gap-2 text-white/80"><Sparkles size={16} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Welcome to your school</span></div>
          <h1 className="font-head text-4xl md:text-5xl font-bold mt-2">How Rising Force Academy works</h1>
          <p className="mt-3 text-white/90 max-w-xl leading-relaxed">Hi{isParent ? "" : `, ${user?.first_name}`}! This is your very own school. Here's everything you can do — it's simple, and a little bit fun. 😊</p>
        </div>

        <h2 className="font-head text-2xl font-bold text-[#3D405B] mb-4">Doing a lesson</h2>
        <div className="space-y-3 mb-12">
          {STEPS.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="flex items-start gap-4 rounded-2xl bg-white border border-[#EAE6DF] p-5">
              <span className="grid place-items-center w-11 h-11 rounded-2xl text-white shrink-0" style={{ background: s.color }}><s.icon size={20} /></span>
              <div><p className="font-head font-semibold text-[#3D405B]">{s.title}</p><p className="text-[#6B7280] text-sm mt-0.5">{s.text}</p></div>
            </motion.div>
          ))}
        </div>

        <h2 className="font-head text-2xl font-bold text-[#3D405B] mb-4">Fun stuff to explore</h2>
        <div className="grid sm:grid-cols-2 gap-3 mb-12">
          {EXTRAS.map((s, i) => (
            <div key={i} className="rounded-2xl bg-white border border-[#EAE6DF] p-5">
              <span className="grid place-items-center w-10 h-10 rounded-2xl text-white mb-3" style={{ background: s.color }}><s.icon size={18} /></span>
              <p className="font-head font-semibold text-[#3D405B]">{s.title}</p>
              <p className="text-[#6B7280] text-sm mt-0.5">{s.text}</p>
            </div>
          ))}
        </div>

        <div className="rounded-3xl p-8 text-white mb-10 grain relative overflow-hidden" style={{ background: "#3D405B" }}>
          <div className="flex items-center gap-2 text-white/80 mb-1"><Award size={18} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Earn Badges</span></div>
          <h2 className="font-head text-3xl font-bold">Collect all 9 badges!</h2>
          <p className="text-white/85 mt-1 mb-6">You earn badges just for showing up and doing your work. Here's how to get each one:</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {BADGES.map((b) => (
              <div key={b.name} className="text-center bg-white/10 rounded-2xl p-3">
                <img src={b.img} alt={b.name} className="w-16 h-16 mx-auto object-contain" />
                <p className="font-head font-semibold text-sm mt-2">{b.name}</p>
                <p className="text-white/75 text-xs mt-0.5">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-[#F2EFE9] border border-[#EAE6DF] p-6 text-center mb-6">
          <Cross size={18} className="mx-auto text-[#E07A5F]" />
          <p className="mt-2 text-[#3D405B] leading-relaxed">Most of all, have fun learning and growing! "Whatever you do, work at it with all your heart, as working for the Lord." <span className="stat text-sm text-[#6B7280]">— Colossians 3:23 (NIV)</span></p>
        </div>

        {!isParent && (
          <button data-testid="guide-start-btn" onClick={() => navigate("/student")}
            className="w-full rounded-full bg-[#81B29A] text-white py-3.5 font-semibold hover:bg-[#6f9d87] transition-colors">
            Got it — let's start! 🎉
          </button>
        )}
      </main>
    </div>
  );
}
